import CustomButton from "components/common/Button/CustomButton";
import DateRangeWithRegister from "components/common/DateRangePicker/DateRangePickerWithRegister";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import { apiPutRequest } from "helpers/Requests";
import { notifyPendingLeaveChanged } from "helpers/pendingLeaveBadge";
import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";

const LeaveUpdateEmployeeModal = (props) => {
  const {
    handleClose,
    leaveId,
    isUserGracePeriodOver,
    workerUuid,
    isOpen,
    onLeaveUpdated,
    leaveData,
  } = props;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    control,
    reset,
    setValue,
    trigger,
    setError,
  } = useForm({
    mode: "onChange",
  });

  const [isLoading, setIsLoading] = useState(false);

  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const is_partial_leave = watch("is_partial_leave");

  useEffect(() => {
    if (is_partial_leave && startDate) {
      setValue("endDate", new Date(startDate));
    }
  }, [is_partial_leave, startDate, setValue]);
  // Populate form with existing leave data
  useEffect(() => {
    if (leaveData) {
      reset({
        startDate: leaveData.start_date || "",
        endDate: leaveData.end_date || "",
        details: leaveData.details || "",
        is_partial_leave: leaveData.is_partial_leave || false, // toggle reflects previous state
        start_time: leaveData.start_time
          ? leaveData.start_time.slice(0, 5)
          : "",
        end_time: leaveData.end_time ? leaveData.end_time.slice(0, 5) : "",
      });
    }
  }, [leaveData, reset]);

  // Trigger validation on date change
  useEffect(() => {
    if (startDate && endDate) {
      trigger(["startDate", "endDate"]);
    }
  }, [startDate, endDate, trigger]);

  useEffect(() => {
    if (is_partial_leave === false && leaveData?.is_partial_leave !== true) {
      setValue("start_time", "");
      setValue("end_time", "");
      trigger(["start_time", "end_time"]);
    }
  }, [is_partial_leave]);

  const handleUpdate = async (data) => {
    try {
      setIsLoading(true);

      const payload = {
        worker_uuid: workerUuid,
        start_date: data.startDate,
        end_date: data.endDate,
        is_partial_leave: data.is_partial_leave,
        start_time: data.is_partial_leave ? data.start_time || null : null,
        end_time: data.is_partial_leave ? data.end_time || null : null,
        details: data.details || null,
      };

      const response = await apiPutRequest(
        `/hrm/worker-leave/${leaveId}`,
        payload
      );

      if (response?.status === 200) {
        notifyPendingLeaveChanged();
        if (onLeaveUpdated) onLeaveUpdated();
        handleClose();
        setIsLoading(false);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error.message;
      toast.error(msg);
      if (msg.includes("End date")) {
        setError("endDate", { type: "backend", message: msg });
      }
      setIsLoading(false);
    }
  };

  const modalFooter = () => (
    <>
 
      <CustomButton clickHandler={handleClose} label='No, cancel changes' variant="secondary" />
           <CustomButton
        showLoading={isLoading}
        isDisabled={isLoading || isUserGracePeriodOver}
        label='Yes, update Leave'
        clickHandler={handleSubmit(handleUpdate)}
        variant='primary'
      />
    </>
  );

  return (
    <>
      {isOpen && (
        <ModalSkeleton
          title='Update Leave '
          cssClass='w-[50%]'
          isOpen={isOpen}
          closeModal={handleClose}
          modalFooter={modalFooter()}
        >
          <div>
            {/* Partial Leave Toggle */}

            {/* Start Date + Start Time */}
            <div className='mt-1 grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <div>
                <label className='did-floating-label'>Start Date</label>
                <Controller
                  name='startDate'
                  control={control}
                  rules={{
                    required: "Start date is required",
                    // validate: (value) =>
                    //   !value
                    //     ? "Start date is required"
                    //     : endDate && new Date(value) > new Date(endDate)
                    //     ? "Start date cannot be after end date"
                    //     : true,
                  }}
                  render={({ field, fieldState }) => (
                    <DateRangeWithRegister
                      isSingleDate
                      field={field}
                      fieldState={fieldState}
                      placeholder='Select Start Date'
                      showCalendarIcon
                      minDate={new Date()}
                    />
                  )}
                />
                {errors?.startDate && (
                  <span className='text-sm text-red-500'>
                    {errors.startDate.message}
                  </span>
                )}
              </div>
              <div>
                <label className='did-floating-label'>End Date</label>
                <Controller
                  name='endDate'
                  control={control}
                  rules={{
                    required: "End date is required",
                    validate: (value) => {
                      if (!value) return "End date is required";

                      const start = new Date(startDate);
                      const end = new Date(value);

                      if (end < start)
                        return "End date cannot be earlier than start date";

                      // NEW RULE: Partial leave ONLY for one day
                      if (
                        is_partial_leave &&
                        start.toDateString() !== end.toDateString()
                      ) {
                        return "Partial leave can only be taken for one day.";
                      }

                      return true;
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <DateRangeWithRegister
                      isSingleDate
                      field={field}
                      fieldState={fieldState}
                      placeholder='Select End Date'
                    isDisabled={watch("is_partial_leave")}
                  showCalendarIcon
                  isClearable={!watch("is_partial_leave")}
                         minDate={
                     new Date(watch("startDate"))
                  
                  }
                       maxDate={
                    watch("is_partial_leave")
                      ? new Date(watch("startDate"))
                      : undefined
                  }
                    />
                  )}
                />
                {errors?.endDate && (
                  <span className='text-sm text-red-500'>
                    {errors.endDate.message}
                  </span>
                )}
              </div>
              <div className='flex items-center gap-2 ' title="Partial leave can not be converted to full day leave">
                <span className='text-sm text-gray-700'>Partial Leave</span>
                <div
                  // onClick={() =>
                  //   setValue("is_partial_leave", !is_partial_leave)
                  // }
                  className={`w-10 h-6 rounded-full opacity-30 cursor-not-allowed transition-colors duration-200 
                  ${is_partial_leave ? "bg-blue-500" : "bg-gray-300"}`}
                >
                  <div
                    className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 mt-1 ml-1
                    ${is_partial_leave ? "translate-x-4" : ""}`}
                  />
                </div>
                <input
                  type='checkbox'
                  disabled={true}
                  {...register("is_partial_leave")}
                  className='sr-only'
                />
              </div>
            </div>

            {is_partial_leave && (
              <div className='mt-1 grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div>
                  <label className='did-floating-label'>Start Time</label>
                  <input
                    type='time'
                    {...register("start_time", {
                      validate: (val) =>
                        !is_partial_leave || (val && val.length)
                          ? true
                          : "Start time is required",
                    })}
                    disabled={!is_partial_leave}
                    className={`did-floating-input w-full ${
                      !is_partial_leave ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onChange={(e) => {
                      register("start_time").onChange(e);
                      trigger("end_time");
                    }}
                  />
                  {errors?.start_time && (
                    <span className='text-sm text-red-500'>
                      {errors.start_time.message}
                    </span>
                  )}
                </div>
                <div>
                  <label className='did-floating-label'>End Time</label>
                  <input
                    type='time'
                    {...register("end_time", {
                      validate: (val) => {
                        if (!is_partial_leave) return true;
                        if (!val || !val.length) return "End time is required";

                        const startTime = watch("start_time");
                        if (startTime && val && val <= startTime) {
                          return "End time must be after start time";
                        }
                        return true;
                      },
                    })}
                    disabled={!is_partial_leave}
                    className={`did-floating-input w-full ${
                      !is_partial_leave ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onChange={(e) => {
                      register("end_time").onChange(e);
                      trigger("end_time");
                    }}
                  />
                  {errors?.end_time && (
                    <span className='text-sm text-red-500'>
                      {errors.end_time.message}
                    </span>
                  )}
                </div>
              </div>
            )}
            {/* {is_partial_leave && (
              <p className='text-xs text-blue-500 mt-1 col-span-2'>
                For partial leave, the End Date can be at most one day after the
                Start Date.
              </p>
            )} */}

            {/* Details */}
            <div className='mt-1 col-span-2 sm:col-span-1'>
              <label className='did-floating-label'>
                Details <span className='text-gray-400'>(optional)</span>
              </label>
              <textarea
                className='did-floating-input'
                style={{ minHeight: "80px", resize: "vertical" }}
                placeholder='Add additional details'
                maxLength={500}
                {...register("details", {
                  maxLength: {
                    value: 500,
                    message: "Reason must be at most 500 characters",
                  },
                })}
              />
            </div>
            {errors?.details && (
              <span className='text-sm text-red-500 mt-1 block'>
                {errors.details.message}
              </span>
            )}
            <div className='text-right text-xs text-gray-500 mt-1'>
              {Math.max(0, 500 - (watch("details")?.length || 0))} characters
              remaining
            </div>
          </div>
        </ModalSkeleton>
      )}
    </>
  );
};

export default LeaveUpdateEmployeeModal;
